import {
  Ban,
  Download,
  FileText,
  LogIn,
  Mail,
  MailCheck,
  MailOpen,
  MailX,
  Pause,
  Play,
  Send,
  SendHorizontal,
  UserMinus,
  UserPlus,
  VideoOff,
  Video,
  CalendarPlus,
  CalendarCog,
  CalendarX,
  TimerOff,
  type LucideIcon,
} from "lucide-react";

/**
 * How each activity event reads (POS2-1138 CP3).
 *
 * **Nineteen types, and the map must be total over them.** The backend `ActivityType` literal is
 * the taxonomy — there is no database `CHECK` on `type` (decision 5), because a widening CHECK would
 * make each new event a migration. That asymmetry only works if the renderer is proven complete, so
 * `activity-events.test.ts` asserts this map covers every key in the literal, reading the Python
 * source rather than a copy of it.
 *
 * Eight of them had **no emitter anywhere yet** — POS2-1136 and POS2-1137 both shipped
 * without theirs (POS2-1234). They are rendered here regardless, and deliberately: a type that
 * reached the database before a screen could render it would show as a raw key on a screen whose
 * whole job is to be readable.
 *
 * `detail` names *the thing* — which report, which recipient, which device. A feed line reading
 * "Email sent" with no address is a line nobody can act on.
 *
 * **English only** — the `/admin/*` carve-out from Phase 6.4.
 */

export type ActivityEntry = {
  id: string;
  occurred_at: string;
  type: string;
  actor_user_id: string | null;
  payload: Record<string, unknown>;
  is_org_wide: boolean;
};

type Descriptor = {
  label: string;
  icon: LucideIcon;
  /** One line naming the subject of the event, or null when the label already says everything. */
  detail: (payload: Record<string, unknown>) => string | null;
};

const text = (payload: Record<string, unknown>, key: string): string | null => {
  const value = payload[key];
  return typeof value === "string" && value !== "" ? value : null;
};

const number = (payload: Record<string, unknown>, key: string): number | null => {
  const value = payload[key];
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
    return Number(value);
  }
  return null;
};

/** "Daily digest for 1–7 Aug", or as much of it as the payload carries. */
const reportSubject = (payload: Record<string, unknown>): string | null => {
  const template = text(payload, "template") ?? text(payload, "report_type");
  const from = text(payload, "period_from");
  const to = text(payload, "period_to");
  if (template && from && to) return `${template} report, ${from} to ${to}`;
  if (template) return `${template} report`;
  return null;
};

const recipient = (payload: Record<string, unknown>): string | null =>
  text(payload, "recipient_email");

export const ACTIVITY_EVENTS: Record<string, Descriptor> = {
  "user.login": {
    label: "User signed in",
    icon: LogIn,
    // The trigger, not the address: the actor column already names who, and whether this row came
    // from a fresh sign-in or a silent token refresh is the thing a reader cannot otherwise tell.
    // Recording refreshes at all is what stops a daily user reading as thirty days quiet.
    detail: (payload) =>
      text(payload, "trigger") === "token_refreshed" ? "Session still active" : null,
  },
  "device.quiet": {
    label: "Device stopped reporting",
    icon: VideoOff,
    detail: (payload) => {
      const serial = text(payload, "device_serial");
      const last = text(payload, "last_capture_at");
      if (serial && last) return `${serial} · last capture ${last.slice(0, 10)}`;
      return serial;
    },
  },
  "device.reporting": {
    label: "Device reporting again",
    icon: Video,
    detail: (payload) => text(payload, "device_serial"),
  },
  "report.generated": { label: "Report generated", icon: FileText, detail: reportSubject },
  // POS2-1316. Somebody pressed "Send to all recipients now". Its own label rather than
  // "Report generated" with a marker, because two "Report generated" rows for one period read as a
  // duplicate-send bug — and this row carries an actor, which a scheduled one never does.
  "report.sent_manually": {
    label: "Report sent manually",
    icon: SendHorizontal,
    detail: (payload) => {
      const subject = reportSubject(payload);
      const count = number(payload, "recipient_count");
      if (subject && count !== null) return `${subject} · ${count} recipients`;
      return subject;
    },
  },
  "email.sent": {
    label: "Email sent",
    icon: Send,
    detail: (payload) => recipient(payload) ?? reportSubject(payload),
  },
  "report.downloaded": {
    label: "Report downloaded",
    icon: Download,
    detail: (payload) => {
      const who = recipient(payload);
      const count = number(payload, "download_count");
      // A repeat read is a different fact from a first one — it is the second half of decision
      // 35's read inference, and worth being able to see in the feed rather than only in a column.
      if (who && count !== null && count > 1) return `${who} · download ${count}`;
      return who;
    },
  },
  "email.delivered": { label: "Email delivered", icon: MailCheck, detail: recipient },
  "email.opened": { label: "Email opened", icon: MailOpen, detail: recipient },
  "email.bounced": {
    label: "Email bounced",
    icon: MailX,
    detail: (payload) => {
      const who = recipient(payload);
      const kind = text(payload, "bounce_type");
      if (who && kind) return `${who} · ${kind} bounce`;
      return who;
    },
  },
  "recipient.unsubscribed": { label: "Recipient unsubscribed", icon: Ban, detail: recipient },
  "recipient.added": { label: "Recipient added", icon: UserPlus, detail: recipient },
  "recipient.removed": { label: "Recipient removed", icon: UserMinus, detail: recipient },
  "schedule.created": { label: "Schedule created", icon: CalendarPlus, detail: reportSubject },
  "schedule.changed": { label: "Schedule changed", icon: CalendarCog, detail: reportSubject },
  "report.paused": { label: "Report paused", icon: Pause, detail: reportSubject },
  "report.resumed": { label: "Report resumed", icon: Play, detail: reportSubject },
  "test.sent": {
    label: "Test send",
    icon: Mail,
    detail: (payload) => recipient(payload) ?? reportSubject(payload),
  },
  // POS2-1257. The schedule was due, its period had already been sent, so it did nothing and moved
  // its cursor on. Names the period, because "which one" is the whole question an AM has when a
  // customer asks why nothing arrived — and a Daily re-timed on the same civil day resolves the
  // same yesterday, which is how this happens in practice.
  "report.period_already_sent": {
    label: "Send skipped, period already sent",
    icon: CalendarX,
    detail: (payload) => {
      const period = text(payload, "period");
      const subject = reportSubject(payload);
      return period && subject ? `${subject} · ${period}` : (period ?? subject);
    },
  },
  // POS2-1298, and the sibling of the line above. The schedule was due, the send was too late to
  // be worth making, so the customer lost that period on purpose. How late says whether this was a
  // deploy going long or something that had been down for a day, which is the first question.
  "report.period_skipped_late": {
    label: "Send skipped, too late to be useful",
    icon: TimerOff,
    detail: (payload) => {
      const period = text(payload, "period");
      const hours = number(payload, "hours_late");
      const subject = reportSubject(payload);
      const what = period && subject ? `${subject} · ${period}` : (period ?? subject);
      if (what && hours !== null) return `${what} · ${hours}h late`;
      return what;
    },
  },
};

/**
 * The descriptor for a type, or a fallback that shows the raw key.
 *
 * Decision 5's asymmetry, made concrete: the taxonomy can grow in the database without a migration,
 * so this screen must degrade to *legible* rather than to blank. A row reading `report.archived`
 * tells a reader what happened and tells us the renderer is behind; an empty row tells nobody
 * anything.
 */
export function describe(type: string): Descriptor {
  return ACTIVITY_EVENTS[type] ?? { label: type, icon: FileText, detail: () => null };
}

/** The filter's options, in the order the map declares them — this ticket's own events first. */
export const ACTIVITY_TYPE_OPTIONS = Object.entries(ACTIVITY_EVENTS).map(([value, { label }]) => ({
  value,
  label,
}));
